interface LogoProps {
  variant?: 'horizontal' | 'compact' | 'icon' | 'white'
  className?: string
}

/**
 * Logo officiel NEXACO Sàrl - Identité premium
 * Emblème circulaire avec lettre N stylisée (style médaillon métallique)
 * Signature: PRÉCISION • FIABILITÉ • ENGAGEMENT
 */
export default function Logo({ variant = 'horizontal', className = '' }: LogoProps) {
  const isWhite = variant === 'white'
  // Sur fond sombre: argent/blanc. Sur fond clair: noir profond
  const mainColor = isWhite ? '#E5E7EB' : '#0A0A0B'
  const subColor = isWhite ? '#9CA3AF' : '#52525B'
  const ringGradId = `nexaco-ring-${variant}`
  const nGradId = `nexaco-n-${variant}`

  // Emblème circulaire avec N stylisé (inspiré du visuel premium)
  const Emblem = (
    <svg
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
      aria-hidden="true"
    >
      <defs>
        {/* Gradient métallique pour l'anneau */}
        <linearGradient id={ringGradId} x1="0%" y1="0%" x2="100%" y2="100%">
          {isWhite ? (
            <>
              <stop offset="0%" stopColor="#F3F4F6" />
              <stop offset="50%" stopColor="#9CA3AF" />
              <stop offset="100%" stopColor="#E5E7EB" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="#1F2937" />
              <stop offset="50%" stopColor="#0A0A0B" />
              <stop offset="100%" stopColor="#374151" />
            </>
          )}
        </linearGradient>
        {/* Gradient pour le N */}
        <linearGradient id={nGradId} x1="0%" y1="0%" x2="100%" y2="100%">
          {isWhite ? (
            <>
              <stop offset="0%" stopColor="#F9FAFB" />
              <stop offset="100%" stopColor="#9CA3AF" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="#0A0A0B" />
              <stop offset="100%" stopColor="#374151" />
            </>
          )}
        </linearGradient>
      </defs>

      {/* Anneau extérieur */}
      <circle
        cx="40"
        cy="40"
        r="37"
        stroke={`url(#${ringGradId})`}
        strokeWidth="3"
        fill="none"
      />
      {/* Anneau intérieur fin */}
      <circle
        cx="40"
        cy="40"
        r="32"
        stroke={subColor}
        strokeWidth="0.5"
        fill="none"
        opacity="0.4"
      />

      {/* Lettre N stylisée — barres verticales gauche/droite + diagonale épaisse */}
      {/* Barre verticale gauche */}
      <rect x="22" y="20" width="7" height="40" fill={`url(#${nGradId})`} />
      {/* Barre verticale droite */}
      <rect x="51" y="20" width="7" height="40" fill={`url(#${nGradId})`} />
      {/* Diagonale - trait épais reliant haut-gauche à bas-droit */}
      <path
        d="M29 20 L58 60 L51 60 L22 20 Z"
        fill={`url(#${nGradId})`}
      />
    </svg>
  )

  if (variant === 'icon') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`} aria-label="NEXACO Sàrl">
        <div className="w-10 h-10">{Emblem}</div>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center gap-2.5 ${className}`} aria-label="NEXACO Sàrl">
        <div className="w-10 h-10 flex-shrink-0">{Emblem}</div>
        <div className="flex flex-col leading-none">
          <span
            className="font-display font-bold text-base tracking-[0.18em]"
            style={{ color: mainColor }}
          >
            NEXACO
          </span>
          <span
            className="text-[9px] font-medium tracking-[0.18em] mt-1"
            style={{ color: subColor }}
          >
            SÀRL
          </span>
        </div>
      </div>
    )
  }

  // horizontal (default) + white share same layout
  return (
    <div className={`inline-flex items-center gap-3 ${className}`} aria-label="NEXACO Sàrl">
      <div className="w-11 h-11 lg:w-12 lg:h-12 flex-shrink-0">{Emblem}</div>
      <div className="flex flex-col leading-none">
        <div className="flex items-baseline gap-1.5">
          <span
            className="font-display font-bold text-xl lg:text-2xl tracking-[0.2em]"
            style={{ color: mainColor }}
          >
            NEXACO
          </span>
          <span
            className="font-display font-medium text-[10px] lg:text-[11px] tracking-[0.15em]"
            style={{ color: subColor }}
          >
            SÀRL
          </span>
        </div>
        <span
          className="text-[9px] lg:text-[10px] font-medium tracking-[0.28em] mt-1.5"
          style={{ color: subColor }}
        >
          PRÉCISION · FIABILITÉ · ENGAGEMENT
        </span>
      </div>
    </div>
  )
}
